import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { ChevronLeft, UserPlus, Users, Check, Clock } from "lucide-react";
import { db } from "../../firebase";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

const UserProfile = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user } = useUserAuth();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friendStatus, setFriendStatus] = useState("none");
  const [stats, setStats] = useState({
    activitiesCount: 0,
    friendsCount: 0,
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const datosRef = doc(db, "Users", uid, uid, "datos");
        const datosSnap = await getDoc(datosRef);

        if (datosSnap.exists()) {
          setProfileData(datosSnap.data());
          await fetchStats();
          await checkFriendStatus();
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [uid, user]);

  const fetchStats = async () => {
    try {
      const activitiesRef = collection(db, "Users", uid, "activities");
      const friendsRef = collection(db, "Users", uid, "friends");

      const [activitiesSnap, friendsSnap] = await Promise.all([
        getDocs(activitiesRef),
        getDocs(friendsRef),
      ]);

      setStats({
        activitiesCount: activitiesSnap.docs.length,
        friendsCount: friendsSnap.docs.length,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const checkFriendStatus = async () => {
    if (!user || user.uid === uid) return;

    try {
      // Verificar si ya son amigos
      const friendRef = doc(db, "Users", user.uid, "friends", uid);
      const friendSnap = await getDoc(friendRef);

      if (friendSnap.exists()) {
        setFriendStatus("friends");
        return;
      }

      // Verificar si hay solicitud pendiente
      const requestRef = doc(db, "Users", uid, "friendsrequest", user.uid);
      const requestSnap = await getDoc(requestRef);

      if (requestSnap.exists()) {
        setFriendStatus("requested");
        return;
      }

      setFriendStatus("none");
    } catch (error) {
      console.error("Error checking friend status:", error);
    }
  };

  const sendFriendRequest = async () => {
    if (!user) return;

    try {
      // Crear la solicitud en el perfil del otro usuario
      const requestRef = doc(db, "Users", uid, "friendsrequest", user.uid);
      await setDoc(requestRef, {
        timestamp: serverTimestamp(),
        status: "pending",
        senderName: user.displayName || "Usuario",
        senderUid: user.uid,
      });

      setFriendStatus("requested");
    } catch (error) {
      console.error("Error sending friend request:", error);
    }
  };

  const acceptFriendRequest = async () => {
    if (!user) return;

    try {
      // Agregar amigo en ambos perfiles
      const timestamp = serverTimestamp();

      await Promise.all([
        // Agregar en mi lista de amigos
        setDoc(doc(db, "Users", user.uid, "friends", uid), {
          timestamp,
          name: profileData.nombre,
          nickname: profileData.nickname,
        }),

        // Agregar en su lista de amigos
        setDoc(doc(db, "Users", uid, "friends", user.uid), {
          timestamp,
          name: user.displayName,
          nickname: user.email, // o el nickname si lo tienes disponible
        }),

        // Eliminar la solicitud
        deleteDoc(doc(db, "Users", user.uid, "friendsrequest", uid)),
      ]);

      setFriendStatus("friends");
      await fetchStats(); // Actualizar contadores
    } catch (error) {
      console.error("Error accepting friend request:", error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-16 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-16">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-2xl mx-auto p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Profile Content */}
      <div className="max-w-2xl mx-auto p-4">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="relative">
              {profileData?.photoUrl ? (
                <img
                  src={profileData.photoUrl}
                  alt={profileData.nombre}
                  className="w-20 h-20 rounded-full object-cover"
                />
              ) : (
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <Users className="w-10 h-10 text-orange-500" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-800">
                {`${profileData?.nombre} ${profileData?.apellido}`}
              </h1>
              {profileData?.nickname && (
                <p className="text-gray-500">@{profileData.nickname}</p>
              )}

              {/* Friend Action Button */}
              {user?.uid !== uid && (
                <div className="mt-4">
                  {friendStatus === "none" && (
                    <button
                      onClick={sendFriendRequest}
                      className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors"
                    >
                      <UserPlus className="w-5 h-5" />
                      Agregar Amigo
                    </button>
                  )}
                  {friendStatus === "requested" && (
                    <button
                      disabled
                      className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-600 rounded-full"
                    >
                      <Clock className="w-5 h-5" />
                      Solicitud Enviada
                    </button>
                  )}
                  {friendStatus === "friends" && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-600 rounded-full">
                      <Check className="w-5 h-5" />
                      Amigos
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="flex justify-around mt-6 pt-6 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-800">
                {stats.activitiesCount}
              </p>
              <p className="text-sm text-gray-500">Actividades</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-800">
                {stats.friendsCount}
              </p>
              <p className="text-sm text-gray-500">Amigos</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="mt-6 space-y-6">
          {/* Activities Section */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                Actividades Recientes
              </h2>
              <button
                onClick={() => navigate(`/user/${uid}/activities`)}
                className="text-orange-500 hover:text-orange-600"
              >
                Ver todas
              </button>
            </div>
          </div>

          {/* Friends Section */}
          <div className="bg-white rounded-2xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Amigos</h2>
              <button
                onClick={() => navigate(`/user/${uid}/friends`)}
                className="text-orange-500 hover:text-orange-600"
              >
                Ver todos
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
